import React, { useState, useEffect } from 'react';
import CustomSingleSelect from '../../../util_components/custom_single_select';
import PackageGroupedSelect from '../../../util_components/package_grouped_select';
import { groupPackageOptions } from '../../../../utils/planCategory';
import { get_api_url } from '../../../../utils/urls';
import { post_api } from '../../../../redux/api_funcs';
import * as actions from '../../../../redux/action_creators';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  useDowngradeOptions,
  PLATFORM_ACCESS_UUID,
  PLATFORM_ACCESS_WEEKS
} from '../../../../utils/useDowngradeOptions';

interface ImmediateDowngradeProps {
  onClose: () => void;
  loadStudentData: () => void;
  student_current_plan: string;
  student_plan_uuid: string;
  is_prepaid: number;
  is_subscribed: number;
  no_of_weeks_originally_enrolled_to: number;
  no_of_cycle_remaining: number;
  studentName?: string;
}

// Mirrors the manual-charge summary the immediate_downgrade API now returns. The
// student is intentionally NOT auto-charged (purchase_price is persisted as 0);
// the amount below is what ops must collect manually via /studenttransactions.
interface ChargeBreakdown {
  downgrade_type: string;
  target_package_uuid: string;
  package_id: number | null;
  no_of_weeks: number | null;
  billing_cycle_days: number | null;
  sessions_per_month: number | null;
  tier_price_field: string | null;
  tier_price: number;
  purchase_price_persisted: number;
  note: string;
}

interface ImmediateDowngradeChargeSummary {
  manual_charge_required: boolean;
  amount_to_charge: number;
  currency: string | null;
  charge_breakdown: ChargeBreakdown;
  student_uuid: string;
  redirect_to: string;
}

function ImmediateDowngrade({
  onClose,
  loadStudentData,
  student_current_plan,
  student_plan_uuid,
  is_prepaid,
  is_subscribed,
  no_of_weeks_originally_enrolled_to,
  no_of_cycle_remaining,
  studentName
}: ImmediateDowngradeProps) {
  const [targetPackageUUID, setTargetPackageUUID] = useState('');
  const [makeStudentMonthly, setMakeStudentMonthly] = useState(false);
  const [noOfWeeks, setNoOfWeeks] = useState('');
  const [reason, setReason] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  // Once the downgrade succeeds we keep the modal open and switch to a summary
  // view built from this so ops can see (and then collect) the manual charge.
  const [chargeSummary, setChargeSummary] = useState<ImmediateDowngradeChargeSummary | null>(null);
  const [downgradedPkgName, setDowngradedPkgName] = useState('');
  const dispatch = useDispatch();
  const history = useHistory();

  const set_notification_variable = (show: boolean, type: 'success' | 'error', mes: string) => {
    dispatch(actions.set_notification_variable(show, type, mes));
  };

  // Plan-selection and number-of-weeks option lists are owned by the shared
  // useDowngradeOptions hook so this modal and Downgrademembership stay identical.
  const {
    planOptions,
    weeksOptions,
    isPlatformAccessSelected,
    getPackageName,
    error: packagesError
  } = useDowngradeOptions({
    student_plan_uuid,
    selectedPackageUUID: targetPackageUUID,
    is_prepaid,
    is_subscribed,
    no_of_weeks_originally_enrolled_to
  });

  // A non-prepaid student is already on monthly billing, so there is nothing to convert — neither
  // the monthly flag nor a cadence is ever sent for them.
  const isAlreadyMonthly = is_prepaid === 0;

  // no_of_weeks is only applied by the immediate_downgrade service for prepaid students who stay
  // prepaid (i.e. not being converted to monthly). Mirror the membership_downgrade modal's cadence
  // options for that case. Platform Access exposes the (locked) weeks field so the fixed value is
  // visible, but only for prepaid students: the service derives make_student_monthly = 1 from a
  // 4-week cadence and then rejects an already-monthly student for having no prepaid record.
  const showWeeksSelect =
    !isAlreadyMonthly && (isPlatformAccessSelected || (is_prepaid === 1 && !makeStudentMonthly));

  // Selecting the 4-week (monthly) cadence is equivalent to converting the student to monthly: a
  // 4-week prepaid record is just monthly billing and is not one of the multi-cycle prepaid tiers.
  // The immediate_downgrade service forces make_student_monthly = 1 for it, so mirror that here and
  // surface it in the UI so the request the operator sees matches what the backend will do.
  const isFourWeekMonthly = !isAlreadyMonthly && Number(noOfWeeks) === 4;
  const effectiveMakeStudentMonthly =
    !isAlreadyMonthly && (makeStudentMonthly || isFourWeekMonthly);

  useEffect(() => {
    setNoOfWeeks(targetPackageUUID === PLATFORM_ACCESS_UUID ? PLATFORM_ACCESS_WEEKS : '');
    setErrorMessage('');
  }, [targetPackageUUID]);

  const handleWeekChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNoOfWeeks(e.target?.value);
  };

  const handleSubmit = async () => {
    if (!targetPackageUUID) {
      return setErrorMessage('Please select a target package.');
    }
    if (reason.trim() === '') {
      return setErrorMessage('Please provide a reason for the downgrade.');
    }
    if (showWeeksSelect && !noOfWeeks) {
      return setErrorMessage('Please select the number of weeks.');
    }
    if (
      showWeeksSelect &&
      targetPackageUUID === student_plan_uuid &&
      parseInt(noOfWeeks || '0', 10) >= Number(no_of_weeks_originally_enrolled_to || 0)
    ) {
      const msg = 'Downgrade is not possible for the same package with equal or more weeks.';
      setErrorMessage(msg);
      set_notification_variable(true, 'error', msg);
      return;
    }

    const id = new URLSearchParams(window.location.search).get('id');
    const apiUrl = get_api_url('immediate_downgrade');
    const payload = {
      student_uuid: id,
      target_package_uuid: targetPackageUUID,
      make_student_monthly: effectiveMakeStudentMonthly ? 1 : 0,
      reason,
      ...(showWeeksSelect ? { no_of_weeks: Number(noOfWeeks) } : {})
    };

    try {
      const response = await post_api(apiUrl, payload, true);
      if (response.data.status === 'success') {
        const pkgName = getPackageName(targetPackageUUID);
        set_notification_variable(true, 'success', `Immediate Downgrade Successful to ${pkgName}`);
        // Flip this modal to the charge summary so ops can collect the amount the
        // API calculated. We intentionally do NOT refresh the profile here:
        // load_student_details() -> load_family_member_list() sets the parent's
        // `loading` flag, which hits `if (loading) return <CustomLoading />` and
        // unmounts this modal, destroying the summary before ops can read it.
        // The refresh is deferred to handleClose() instead.
        setDowngradedPkgName(pkgName);
        setChargeSummary(response.data as ImmediateDowngradeChargeSummary);
      }
    } catch (error) {
      console.log('error', error.response);
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        'Something went wrong. Please try again.';
      setErrorMessage(msg);
      set_notification_variable(true, 'error', msg);
    }
  };

  // Refresh the underlying profile only when ops dismisses the summary. Doing it
  // earlier (e.g. right after submit) toggles the parent's `loading` flag, which
  // remounts this modal and wipes the charge summary before it can be read.
  const handleClose = () => {
    loadStudentData();
    onClose();
  };

  // Route ops to the transactions page to actually collect the money. The amount
  // and a suggested reason ride along as query params so the charge modal there
  // opens pre-filled (the backend persisted purchase_price = 0 on purpose).
  const goToTransactions = () => {
    if (!chargeSummary) return;
    const studentId =
      chargeSummary.student_uuid || new URLSearchParams(window.location.search).get('id') || '';
    const params = new URLSearchParams({
      id: studentId,
      name: studentName || '',
      charge_amount: String(chargeSummary.amount_to_charge),
      charge_reason: `Immediate downgrade${downgradedPkgName ? ` to ${downgradedPkgName}` : ''} - manual collection`
    });
    history.push({
      pathname: chargeSummary.redirect_to || '/studenttransactions',
      search: `?${params.toString()}`
    });
    onClose();
  };

  if (chargeSummary) {
    const { charge_breakdown: breakdown } = chargeSummary;
    const currencyLabel = chargeSummary.currency ? `${chargeSummary.currency.toUpperCase()} ` : '';
    const breakdownRows: Array<[string, string | number]> = [
      ['Downgrade type', breakdown?.downgrade_type],
      ['No. of weeks', breakdown?.no_of_weeks],
      ['Sessions / month', breakdown?.sessions_per_month],
      ['Billing cycle (days)', breakdown?.billing_cycle_days],
      ['Price basis', breakdown?.tier_price_field]
    ].filter(([, value]) => value !== null && value !== undefined && value !== '') as Array<
      [string, string | number]
    >;

    return (
      <div className="w-[430px]" style={{ color: 'var(--text-primary)' }}>
        <hr className="mb-5" style={{ borderColor: 'var(--border-default)' }} />

        <div
          className="rounded-[8px] p-3 mb-4 text-sm"
          style={{ background: 'var(--state-success-bg)', color: 'var(--state-success-text)' }}
        >
          <strong>Downgrade complete{downgradedPkgName ? ` to ${downgradedPkgName}` : ''}.</strong>{' '}
          The student was not charged automatically.
        </div>

        {chargeSummary.manual_charge_required ? (
          <>
            <div className="mb-1 text-sm" style={{ color: 'var(--text-muted)' }}>
              Amount to collect manually
            </div>
            <div className="mb-4 text-2xl font-bold" style={{ color: 'var(--brand-primary)' }}>
              {currencyLabel}
              {chargeSummary.amount_to_charge}
            </div>

            <div
              className="rounded-[8px] p-3 mb-4 text-sm"
              style={{
                background: 'var(--surface-card)',
                border: '1px solid var(--border-default)'
              }}
            >
              <div className="mb-2 font-semibold">Breakdown</div>
              {breakdownRows.map(([label, value]) => (
                <div key={label} className="flex justify-between py-1">
                  <span style={{ color: 'var(--text-muted)' }}>{label}</span>
                  <span className="font-medium text-right">{String(value)}</span>
                </div>
              ))}
            </div>

            {breakdown?.note && (
              <div
                className="rounded-[8px] p-3 mb-5 text-xs"
                style={{ background: 'var(--state-info-bg)', color: 'var(--state-info-text)' }}
              >
                {breakdown.note}
              </div>
            )}

            <div className="flex justify-center gap-4">
              <button
                onClick={handleClose}
                className="w-full px-6 py-2 rounded"
                style={{
                  border: '1px solid var(--border-default)',
                  color: 'var(--text-primary)',
                  background: 'transparent'
                }}
              >
                Close
              </button>
              <button
                onClick={goToTransactions}
                className="w-full px-6 py-2 rounded text-white"
                style={{ background: 'var(--brand-primary)' }}
              >
                Go to Transactions to Charge
              </button>
            </div>
          </>
        ) : (
          <>
            <div
              className="rounded-[8px] p-3 mb-5 text-sm"
              style={{ background: 'var(--state-info-bg)', color: 'var(--state-info-text)' }}
            >
              No manual charge is required for this downgrade.
            </div>
            <div className="flex justify-center">
              <button
                onClick={handleClose}
                className="w-full px-6 py-2 rounded text-white"
                style={{ background: 'var(--brand-primary)' }}
              >
                Close
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="w-[430px]">
      <hr className="mb-5 border-t border-gray-300" />

      <div className="mb-3">
        <b>Current Plan:</b> <span>{student_current_plan}</span>
        <span>{is_prepaid === 0 ? `/Monthly` : null}</span>
      </div>

      {is_prepaid === 1 && (
        <>
          <div className="mb-3">
            <b>No. of weeks enrolled to:</b> <span>{no_of_weeks_originally_enrolled_to}</span>
          </div>

          <div className="mb-3">
            <b>No. of billing cycles left:</b> <span>{no_of_cycle_remaining}</span>
          </div>
        </>
      )}

      <PackageGroupedSelect
        label="Target package plan"
        groups={groupPackageOptions(planOptions, student_plan_uuid)}
        defaultOption="Select an option"
        onChange={(e) => setTargetPackageUUID(e.target?.value)}
        name="target_package_uuid"
        value={targetPackageUUID}
        style={{ width: '100%' }}
      />

      {showWeeksSelect && (
        <CustomSingleSelect
          label="Number of weeks"
          options={weeksOptions.map((week) => ({
            label: week.toString(),
            value: week.toString()
          }))}
          onChange={handleWeekChange}
          name="no_of_weeks"
          value={noOfWeeks}
          style={{ width: '100%' }}
          defaultOptionDisabled={isPlatformAccessSelected}
        />
      )}

      {!isAlreadyMonthly && (
        <div className="mb-3 mt-4 flex items-center gap-2">
          <input
            type="checkbox"
            id="make_student_monthly"
            checked={effectiveMakeStudentMonthly}
            disabled={isFourWeekMonthly}
            onChange={(e) => setMakeStudentMonthly(e.target.checked)}
            className="w-4 h-4 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
          />
          <label
            htmlFor="make_student_monthly"
            className="text-sm font-semibold text-gray-700 cursor-pointer"
          >
            Make student monthly
          </label>
        </div>
      )}

      {isFourWeekMonthly && (
        <p className="mb-3 -mt-1 text-xs text-gray-500">
          A 4-week cadence is monthly billing, so the student will be converted to monthly
          automatically.
        </p>
      )}

      <div className="mb-3">
        <label
          className="block mt-4 mb-1 text-sm font-semibold text-gray-700"
          style={{ textTransform: 'unset' }}
        >
          Reason For Downgrade <span className="text-[red]">*</span>
        </label>
        <textarea
          className="h-[80px] border border-gray-300 rounded-md w-full p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Enter the reason for the downgrade..."
        />
      </div>

      <div className="bg-yellow-100 border border-[#ffef8a] rounded-[8px] p-3 mb-5 text-sm text-yellow-900">
        <strong>Warning:</strong> This action will immediately downgrade the student's membership.
      </div>

      {(errorMessage || packagesError) && (
        <div className="p-3 mt-5 mb-5 text-red-800 bg-red-100 rounded">
          <strong>Error:</strong> {errorMessage || packagesError}
        </div>
      )}

      <div className="flex justify-center gap-4">
        <button
          onClick={onClose}
          className="w-full px-6 py-2 text-black bg-gray-200 border border-gray-400 rounded hover:bg-gray-300"
        >
          Cancel
        </button>

        <button
          onClick={handleSubmit}
          disabled={!targetPackageUUID || !reason.trim() || (showWeeksSelect && !noOfWeeks)}
          className={`px-6 py-2 w-full rounded border text-white
            ${
              !targetPackageUUID || !reason.trim() || (showWeeksSelect && !noOfWeeks)
                ? 'bg-red-600 border-red-700 opacity-60 cursor-not-allowed'
                : 'bg-red-600 border-red-700 hover:bg-red-700 cursor-pointer'
            }`}
        >
          Submit
        </button>
      </div>
    </div>
  );
}

export default ImmediateDowngrade;
